import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import QRCode from 'qrcode';
import SponsoredVideoPreview from './SponsoredVideoPreview';
import { formatTimestamp, resolveActivePromotion, sortPromotions } from './timelineUtils';

export default function SponsoredTimelineEditor({ video, busy, onSave, onDuration }) {
  const previewRef = useRef(null);
  const onDurationRef = useRef(onDuration);
  const [promotions, setPromotions] = useState(video.promotions || []);
  const [currentTime, setCurrentTime] = useState(0);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const active = useMemo(() => resolveActivePromotion(promotions, currentTime), [promotions, currentTime]);

  useEffect(() => setPromotions(video.promotions || []), [video]);
  useEffect(() => { onDurationRef.current = onDuration; }, [onDuration]);
  useEffect(() => {
    if (!active?.publicKey) return setQrDataUrl('');
    const url = `${window.location.origin}/go/sp/${active.publicKey}`;
    QRCode.toDataURL(url, { width: video.qrSize, margin: 1, errorCorrectionLevel: 'M' }).then(setQrDataUrl);
  }, [active?.publicKey, video.qrSize]);

  const update = (index, field, value) => {
    setPromotions((rows) => sortPromotions(rows.map((row, rowIndex) => rowIndex === index ? { ...row, [field]: value } : row)));
  };
  const add = () => {
    const time = Number((previewRef.current?.getCurrentTime() || 0).toFixed(3));
    if (promotions.some((promotion) => Number(promotion.startSeconds).toFixed(3) === time.toFixed(3))) return;
    setPromotions(sortPromotions([...promotions, { startSeconds: time, message: '', destinationUrl: '' }]));
  };
  const handleDuration = useCallback((duration) => {
    if (Number.isFinite(duration) && duration > 0) onDurationRef.current(duration);
  }, []);

  return (
    <div className="sponsored-timeline">
      <h3>Promotion timeline</h3>
      <div className="sponsored-preview">
        <SponsoredVideoPreview ref={previewRef} video={video} onTimeChange={setCurrentTime} onDuration={handleDuration} />
        {active && <div className="sponsored-preview__overlay" style={{ background: `rgba(15, 23, 42, ${(100 - video.transparency) / 100})`, fontSize: video.textSize, width: `${video.widthPercent}%` }}><span>{active.message || 'Promotion message'}</span>{qrDataUrl && <img src={qrDataUrl} style={{ width: video.qrSize, height: video.qrSize }} alt="Promotion QR preview" />}</div>}
      </div>
      <div className="sponsored-timeline__toolbar"><span>Current time: {formatTimestamp(currentTime)}</span><button type="button" onClick={add}>Add promotion at current time</button></div>
      {promotions.map((promotion, index) => (
        <div className="sponsored-promotion-row" key={promotion.publicKey || `${promotion.startSeconds}-${index}`}>
          <button type="button" onClick={() => previewRef.current?.seekTo(Number(promotion.startSeconds))}>Seek</button>
          <label>Start (seconds)<input type="number" min="0" step="0.001" value={promotion.startSeconds} onChange={(event) => update(index, 'startSeconds', event.target.value)} /></label>
          <label>Message<input maxLength="500" value={promotion.message} onChange={(event) => update(index, 'message', event.target.value)} /></label>
          <label>GetYourGuide URL<input value={promotion.destinationUrl} onChange={(event) => update(index, 'destinationUrl', event.target.value)} /></label>
          <button type="button" onClick={() => setPromotions((rows) => rows.filter((_, rowIndex) => rowIndex !== index))}>Delete</button>
        </div>
      ))}
      <button type="button" disabled={busy || promotions.some((promotion) => !promotion.message || !promotion.destinationUrl)} onClick={() => onSave(sortPromotions(promotions))}>Save promotion timeline</button>
    </div>
  );
}
